import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

/**
 * Component to help with pagination with inputs:
 * - collectionSize: number of total items to be displayed
 * - pageSize: number of items displayed per page
 * - page: the current page number
 * - pageSizeChoices: array of page size choices for user to select (default to [5, 10])
 *
 * Emits:
 * - pageChange: the new current page number
 * - pageSizeChange: the new page size
 */
@Component({
	selector: 'ac-pagination',
	templateUrl: './pagination.component.html',
	styleUrls: ['./pagination.component.scss']
})
export class PaginationComponent implements OnChanges {
	@Input() public collectionSize: number;
	@Input() public pageSize: number;
	@Input() public page: number = 1;
	@Input() public pageSizeChoices: number[] = [5, 10];
	@Output() public pageChange: EventEmitter<number> = new EventEmitter<number>();
	@Output() public pageSizeChange: EventEmitter<number> = new EventEmitter<number>();
	public pageCount: number = 0;
	public pages: number[] = [];

	public ngOnChanges(): void {
		this.updatePages(this.page);
	}

	public hasPrevious(): boolean {
		return this.page > 1;
	}

	public hasNext(): boolean {
		return this.page < this.pageCount;
	}

	public nextDisabled(): boolean {
		return !this.hasNext();
	}

	public previousDisabled(): boolean {
		return !this.hasPrevious();
	}

	public selectPage(pageNumber: number): void {
		this.updatePages(pageNumber);
	}

	public updatePages(newPage: number): void {
		this.pageCount = Math.ceil(this.collectionSize / this.pageSize);

		if (isNaN(this.pageCount)) {
			this.pageCount = 0;
		}

		let maxShownPage = newPage + 3;
		let minShownPage = newPage - 3;

		if (minShownPage <= 1 && maxShownPage <= this.pageCount) {
			minShownPage = 1;
			maxShownPage = 7;
		}

		if (maxShownPage > this.pageCount) {
			maxShownPage = this.pageCount;
			minShownPage = this.pageCount - 6;
		}

		if (minShownPage < 1) {
			minShownPage = 1;
		}

		this.pages.length = 0;
		for (let i = minShownPage; i <= maxShownPage; i++) {
			this.pages.push(i);
		}

		this.page = newPage;
		this.pageChange.emit(this.page);
	}

	public selectSize(sizeNumber: string): void {
		this.pageSize = parseInt(sizeNumber, 10);
		this.pageSizeChange.emit(this.pageSize);
		this.selectPage(1);
	}
}
